import { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';
import { useFeature } from '@/components/FeatureGate';
import { getErrorMessage } from '@/lib/networkError';

export interface Tag {
  id: string;
  user_id: string;
  name: string;
  color: string;
  is_preset: boolean;
  created_at: string;
}

const PRESET_TAGS = [
  { name: 'Personal', color: '#22c55e' },
  { name: 'Work', color: '#3b82f6' },
  { name: 'Church', color: '#a855f7' },
  { name: 'Sales', color: '#f59e0b' },
  { name: 'Design', color: '#ec4899' },
  { name: 'Marketing', color: '#06b6d4' },
];

const FREE_TAG_LIMIT = 3;

export type TagsContextValue = {
  tags: Tag[];
  loading: boolean;
  addTag: (name: string, color: string) => Promise<Tag | null>;
  updateTag: (id: string, updates: { name?: string; color?: string }) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
};

const TagsContext = createContext<TagsContextValue | null>(null);

export function TagsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const hasUnlimitedTags = useFeature('unlimitedTags');
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTags = useCallback(async () => {
    if (!user) {
      setTags([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('user_id', user.id)
        .order('is_preset', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;

      // If no tags exist, initialize with presets
      if (!data || data.length === 0) {
        const initializePresets = async () => {
          if (!user) return;
          try {
            const presetInserts = PRESET_TAGS.map(tag => ({
              user_id: user.id,
              name: tag.name,
              color: tag.color,
              is_preset: true,
            }));
            const { data: inserted, error: insertError } = await supabase
              .from('tags')
              .insert(presetInserts)
              .select();
            if (insertError) throw insertError;
            setTags(inserted as Tag[]);
          } catch (err) {
            console.error('Error initializing preset tags:', err);
          }
        };
        await initializePresets();
        setLoading(false);
        return;
      }

      // De-duplicate tags by name (case-insensitive), keeping the first occurrence
      const seen = new Set<string>();
      const uniqueTags = (data as Tag[]).filter(tag => {
        const lowerName = tag.name.toLowerCase();
        if (seen.has(lowerName)) return false;
        seen.add(lowerName);
        return true;
      });

      setTags(uniqueTags);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast({
        title: 'Error loading tags',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const addTag = useCallback(async (name: string, color: string) => {
    if (!user) return null;

    const trimmedName = name.trim();
    const customTagCount = tags.filter((t) => !t.is_preset).length;
    if (!hasUnlimitedTags && customTagCount >= FREE_TAG_LIMIT) {
      toast({
        title: 'Tag limit reached',
        description: `Basic includes ${FREE_TAG_LIMIT} tags. Upgrade to Pro for unlimited.`,
        variant: 'destructive',
      });
      return null;
    }

    const isDuplicate = tags.some(
      tag => tag.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      toast({
        title: 'Tag already exists',
        description: `A tag named "${trimmedName}" already exists.`,
        variant: 'destructive',
      });
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('tags')
        .insert({
          user_id: user.id,
          name: trimmedName,
          color,
          is_preset: false,
        })
        .select()
        .single();

      if (error) throw error;

      setTags(prev => [...prev, data as Tag]);
      return data as Tag;
    } catch (error) {
      console.error('Error adding tag:', error);
      toast({
        title: 'Error adding tag',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
      return null;
    }
  }, [user, tags, hasUnlimitedTags, toast]);

  const updateTag = useCallback(async (id: string, updates: { name?: string; color?: string }) => {
    if (updates.name !== undefined) {
      const trimmed = updates.name.trim();
      const isDuplicate = tags.some(
        (t) => t.id !== id && t.name.toLowerCase() === trimmed.toLowerCase()
      );
      if (isDuplicate) {
        toast({
          title: 'Tag already exists',
          description: `A tag named "${trimmed}" already exists.`,
          variant: 'destructive',
        });
        return;
      }
      updates = { ...updates, name: trimmed };
    }

    try {
      const { error } = await supabase
        .from('tags')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setTags(prev => prev.map(t => (t.id === id ? { ...t, ...updates } : t)));
    } catch (error) {
      console.error('Error updating tag:', error);
      toast({
        title: 'Error updating tag',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  }, [tags, toast]);

  const deleteTag = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setTags(prev => prev.filter(t => t.id !== id));
      // Notify so tasks list updates in real time (optimistic + refetch)
      window.dispatchEvent(new CustomEvent('tag-deleted', { detail: { tagId: id } }));
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: 'Error deleting tag',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  }, [toast]);

  const value = useMemo(
    () => ({
      tags,
      loading,
      addTag,
      updateTag,
      deleteTag,
      refetch: fetchTags,
    }),
    [tags, loading, addTag, updateTag, deleteTag, fetchTags]
  );

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

export function useTags(): TagsContextValue {
  const ctx = useContext(TagsContext);
  if (!ctx) {
    throw new Error('useTags must be used within TagsProvider');
  }
  return ctx;
}
